/**
 * The phone lesson preview's text and box rules (spec 13.5). Pure apart from
 * the aspect memory, which keeps each clip's shape so the video box is the
 * right size from its first paint and never resizes when metadata arrives.
 */

export interface PreviewClipLike {
  id: string;
  segmentIds: string[];
  label?: string;
  isCameraSource?: boolean;
  videoDuration?: number;
}
export interface PreviewSegmentLike { id: string; label: string }

/** The recording's own name, numbered within its segment the way the organizer numbers it. */
export function clipName(clip: PreviewClipLike, allClips: PreviewClipLike[]): string {
  if (clip.label) return clip.label;
  if (clip.isCameraSource) return 'Camera Recording';
  const same = allClips.filter((c) => c.segmentIds[0] === clip.segmentIds[0]);
  return `Recording ${same.findIndex((c) => c.id === clip.id) + 1}`;
}

/** "Part 1 · Recording 1": the segment's name, then the recording's. */
export function previewClipTitle(clip: PreviewClipLike, allClips: PreviewClipLike[], segments: PreviewSegmentLike[]): string {
  const si = segments.findIndex((s) => s.id === clip.segmentIds[0]);
  const name = clipName(clip, allClips);
  /* rowText.rowName's rule, inlined: this file has no imports so the unit tests run under node. */
  return si < 0 ? name : `${segments[si].label?.trim() || `Part ${si + 1}`} · ${name}`;
}

/** m:ss, rounded to the nearest second; empty when the length is unknown. */
export function clipLength(seconds: number | undefined): string {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return '';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

const KEY = 'lve-clip-aspects';
let memory: Record<string, number> | null = null;
function load(): Record<string, number> {
  if (memory) return memory;
  try { memory = JSON.parse(globalThis.localStorage?.getItem(KEY) ?? '{}') ?? {}; } catch { memory = {}; }
  return memory!;
}

/** Remembers a clip's width / height, and that it is the last one seen. */
export function rememberAspect(clipId: string, aspect: number): void {
  if (!(aspect > 0) || !Number.isFinite(aspect)) return;
  const m = load();
  m[clipId] = aspect;
  m.__last = aspect;
  try { globalThis.localStorage?.setItem(KEY, JSON.stringify(m)); } catch { /* per-viewer convenience only */ }
}

/** This clip's shape if it has been seen, else the last shape seen, else 16:9. */
export function aspectFor(clipId: string): number {
  const m = load();
  return m[clipId] ?? m.__last ?? 16 / 9;
}

export function hasAspect(clipId: string): boolean {
  return load()[clipId] !== undefined;
}
